<template>
    <settings-wrapper title="Space settings" section="Additional services">
        <v-row>
            <v-col>
                <h3>Integrations</h3>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" sm="6" lg="4" xl="3">
                <v-card flat elevation="3" class="ma-1">
                    <v-list three-line color="transparent">
                        <v-list-item>
                            <v-list-item-avatar>
                                <v-icon class="primary lighten-1" dark>{{ isDropboxLinked ? 'mdi-check' : 'mdi-circle-outline' }}</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>Dropbox</v-list-item-title>
                                <v-list-item-subtitle>
                                    Each user of a space needs to enable Dropbox synchronization separately for their own account.
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                    <v-card-actions>
                        <v-btn text color="primary" v-if="!isDropboxLinked" :to="{ name: 'user-dropbox' }">Enable</v-btn>
                        <v-btn text color="secondary" disabled v-else>Enabled</v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
        <div v-for="(name, id) in featureGroups" :key="id">
            <v-row class="mt-4">
                <v-col>
                    <h3>{{ name }}</h3>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" sm="6" lg="4" xl="3" v-for="(feature, index) in featuresByGroup(id)" :key="index">
                    <org-feature-status :feature="feature" :enableOnly="true" level="space" />
                </v-col>
            </v-row>
        </div>
    </settings-wrapper>
</template>

<script>
import { dropboxEnums } from '@/mixins/dropbox'
import { mapState } from 'vuex'
import eventBus from '@/components/EventBus'
import { fetchTask } from '@/apis'
import { isAdminRole } from '@/utils'
import OrgFeatureStatus from '@/components/OrgFeatureStatus.vue'
import SettingsWrapper from '@/components/SettingsWrapper.vue'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as spaceErrors from '@/modules/space/errors'

const { getBackendErrorMessage } = createBackendErrorHandler(lifecycleErrors, permissionsErrors, spaceErrors)

const featureProperties = {
    loading: false,
    enabled: false,
    canEnable: false,
    disabledReason: '',
    error: '',
    fn: () => {}
}

export default {
    name: 'SpaceConfigurationFeatures',
    mixins: [dropboxEnums],
    components: {
        SettingsWrapper,
        OrgFeatureStatus
    },
    data() {
        return {
            switchingFeature: '',
            featureGroups: {
                HPC: 'Compute',
                EXTRAS: 'Additional services'
            },
            features: {
                vimeo: {
                    ...featureProperties,
                    label: 'Video Library',
                    description: 'Upload videos to make your content more interactive on Nuvolos.',
                    endpointProperty: 'vimeo_enabled',
                    group: 'EXTRAS'
                },
                tables: {
                    ...featureProperties,
                    label: 'Database Tables',
                    description: 'Use the Scientific Data Warehouse to work with massive amounts of tabular data.',
                    endpointProperty: 'has_tables',
                    cb: this.activateTable,
                    group: 'EXTRAS'
                },
                hpc: {
                    ...featureProperties,
                    label: 'Credit-based sizes',
                    description: 'Scale up your application resources using credits.',
                    endpointProperty: 'beegfs_sync',
                    group: 'HPC'
                }
            }
        }
    },
    async created() {
        this.checkDropboxStatus()
        if (this.activeOrgId && !this.spacesWithPrimarySnapshots.length) {
            await this.$store.dispatch('orgStore/fetchOrgSpaces', this.activeOrgId)
        }

        eventBus.$on('global-dialog-accept', () => {
            if (this.switchingFeature) {
                this.enableFeature()
            } else {
                this.switchingFeature = ''
            }
        })

        if (this.activeSpaceId) {
            this.loadActiveSpaceContext()
        }
    },
    beforeDestroy() {
        eventBus.$off('global-dialog-accept')
    },
    methods: {
        ensureAdminAccess() {
            if (!this.activeSpaceId || !this.activeSpaceData) {
                return
            }
            if (this.isSpaceAdmin === false) {
                this.$router.replace({
                    name: 'config-space-general',
                    params: {
                        oid: this.activeOrgId,
                        sid: this.activeSpaceId
                    },
                    query: { ...this.$route.query }
                })
            }
        },
        confirmFeatureSwitch(feature) {
            return async () => {
                this.switchingFeature = feature
                const dialogTitle = `Enabling ${this.features[feature].label}`
                const dialogText = `Please note that this feature cannot be turned off in self-service, however you're free to release the resources in it anytime to control (potential) costs.`
                this.$store.dispatch('showGlobalDialog', {
                    dialogTitle,
                    dialogText,
                    dialogAction: ['cancel', 'confirm']
                })
            }
        },
        async enableFeature() {
            const feature = this.switchingFeature
            const sid = this.activeSpaceId
            if (!sid) {
                return
            }
            const f = this.features[feature]
            f.loading = true

            if (f.cb) return f.cb()

            try {
                const { data } = await this.$axios.patch(`spaces/${sid}`, {
                    [this.features[feature].endpointProperty]: !f.enabled
                })
                f.loading = false
                this.$store.commit('spaceStore/setCurrentSpace', data)
            } catch (error) {
                const snackBarText = getBackendErrorMessage(
                    error,
                    `Failed to ${this.features[feature].enabled ? 'disable' : 'enable'} ${this.features[feature].label.toLowerCase()}, please try again later!`
                )
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
                f.loading = false
                this.switchingFeature = ''
            }
        },
        fetchStatusOfFeature(feature) {
            if (!this.activeSpaceId) {
                return
            }
            const f = this.features[feature]
            f.loading = true
            f.fn = this.confirmFeatureSwitch(feature)
            this.$axios
                .get(`/spaces/${this.activeSpaceId}/can_enable_${feature}`)
                .then(({ data }) => {
                    if (this.currentSpace) f.enabled = this.currentSpace[f.endpointProperty]
                    f.canEnable = data[`can_enable_${feature}`]
                    f.disabledReason = data.reason
                    f.loading = false
                })
                .catch(error => {
                    f.loading = false
                    f.error = error.message
                })
        },
        async activateTable() {
            if (!this.activeSpaceId) {
                return
            }
            this.$store.dispatch('showSnackBar', {
                snackBarText: 'Activating tables...',
                snackBarTimeout: 5000,
                snackBarIcon: 'info'
            })
            const { data } = await this.$axios.get(`/spaces/${this.activeSpaceId}/activate_async`)
            const taskResult = await fetchTask(data.tkid)
            if (taskResult instanceof Error) {
                this.features.tables.error = taskResult.message
            } else {
                this.features.tables.enabled = true
                this.features.tables.loading = false
                this.$store.dispatch('orgStore/fetchOrgSpaces', this.activeOrgId)
            }
        },
        featuresByGroup(group) {
            const features = []
            for (const feature in this.features) {
                if (this.features[feature].group === group) features.push(this.features[feature])
            }
            return features
        },
        async loadActiveSpaceContext() {
            if (!this.activeSpaceId) {
                return
            }
            try {
                const { data } = await this.$axios.get(`/spaces/${this.activeSpaceId}`)
                this.$store.commit('spaceStore/setCurrentSpace', data)
                Object.keys(this.features).forEach(this.fetchStatusOfFeature)
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, 'Failed to load selected space configuration.')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            }
        }
    },
    computed: {
        ...mapState('orgStore', ['spacesWithPrimarySnapshots']),
        ...mapState('spaceStore', ['currentSpace']),
        activeOrgId() {
            return this.$route.params.oid
        },
        activeSpaceId() {
            return this.$route.params.sid
        },
        activeSpaceData() {
            if (!this.activeSpaceId) {
                return null
            }
            return this.spacesWithPrimarySnapshots.find(space => space.sid.toString() === this.activeSpaceId.toString()) || null
        },
        isSpaceAdmin() {
            return isAdminRole(this.activeSpaceData?.space_role)
        }
    },
    watch: {
        spacesWithPrimarySnapshots: {
            immediate: true,
            handler(spaces) {
                if (!this.$route.params.sid && Array.isArray(spaces) && spaces.length) {
                    this.$router.replace({
                        name: this.$route.name,
                        params: {
                            ...this.$route.params,
                            sid: spaces[0].sid
                        },
                        query: { ...this.$route.query }
                    })
                }
            }
        },
        activeSpaceId: {
            immediate: true,
            handler() {
                this.ensureAdminAccess()
                if (this.activeSpaceId) {
                    this.loadActiveSpaceContext()
                }
            }
        },
        activeSpaceData: {
            immediate: true,
            handler() {
                this.ensureAdminAccess()
            }
        },
        currentSpace: {
            immediate: true,
            handler(val) {
                if (!val) {
                    return
                }
                this.features.vimeo.enabled = val.vimeo_enabled
                this.features.hpc.enabled = val.beegfs_sync
                this.features.tables.enabled = val.has_tables
            }
        }
    }
}
</script>
